import type { PublicStorefront } from '../../services/storefrontService';

/** Reservierte Pfadsegmente hinter `/store/` (kein Slug). */
const RESERVED = new Set(['api', 'admin', 'login', 'register', 'checkout', 'cart', 'catalog', 'products', 'orders', 'store', 'p']);

/**
 * Storefront-Slug aus dem Pfad: `/store/{slug}[/…]`. Liefert null für `/store`
 * ohne Slug oder reservierte Segmente (Custom-Domain-Auflösung folgt, sobald SSO
 * die Company-ID im öffentlichen Branding liefert).
 */
export function resolveStorefrontSlug(pathname: string): string | null {
    const match = /^\/store\/([^/?#]+)/.exec(pathname);
    if (!match) return null;
    const slug = decodeURIComponent(match[1]).toLowerCase();
    return RESERVED.has(slug) ? null : slug;
}

export interface BrandingLike {
    logoUrl: string | null;
    primaryColor: string;
    secondaryColor: string;
    domain?: string | null;
}

export interface ResolvedStorefrontBranding {
    logoUrl: string | null;
    primaryColor: string;
    secondaryColor: string;
}

const HEX = /^#[0-9a-fA-F]{3,8}$/;

/**
 * Mandanten-Branding der Storefront: Auf der Mandanten-Domain liefert der
 * `BrandingContext` (SSO, per Domain) das maßgebliche Branding; sonst fällt die
 * Seite auf das gespiegelte Branding der Storefront-API zurück.
 */
export function resolveStorefrontBranding(context: BrandingLike, storefront: PublicStorefront['branding'] | null | undefined): ResolvedStorefrontBranding {
    const onTenantDomain = !!context.domain;
    const pick = (ctx: string | null, api: string | null | undefined, fallback: string) => {
        const first = onTenantDomain ? ctx : api;
        const second = onTenantDomain ? api : ctx;
        return [first, second].find((c): c is string => !!c && HEX.test(c)) ?? fallback;
    };
    return {
        logoUrl: (onTenantDomain ? context.logoUrl : storefront?.logoUrl) || context.logoUrl || storefront?.logoUrl || null,
        primaryColor: pick(context.primaryColor, storefront?.primaryColor, '#1976d2'),
        secondaryColor: pick(context.secondaryColor, storefront?.secondaryColor, '#dc004e'),
    };
}

export function formatMoney(amount: string | number, currency = 'EUR', locale = 'de-DE'): string {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(amount));
}

// ---- Warenkorb (nur Browser, kein Server-Zustand — Plan E9: Storefront-Kanal) ----

const cartKey = (slug: string) => `oa-storefront-cart:${slug}`;

export function readCart(slug: string): number[] {
    try {
        const raw = JSON.parse(localStorage.getItem(cartKey(slug)) || '[]');
        return Array.isArray(raw) ? Array.from(new Set(raw.map(Number).filter((n) => Number.isInteger(n) && n > 0))) : [];
    } catch {
        return [];
    }
}

export function writeCart(slug: string, ids: number[]): void {
    try {
        localStorage.setItem(cartKey(slug), JSON.stringify(Array.from(new Set(ids))));
    } catch {
        /* privater Modus: Warenkorb bleibt flüchtig */
    }
}
